import type { CSSProperties } from 'react';
import { darkTheme } from '../../theme/tokens';
import { Badge } from '../ui/Badge';
import type { DiffActionVerb, FindingDiff } from '../../types';
import { FieldLabel, ScopeRow, ValueChip, ValueDiff } from '../findings/bodies/elements';
import { stringifyCell } from './diffCell';

const t = darkTheme;

interface DiffCardProps {
  diff: FindingDiff;
}

/**
 * The before/after card for an entity-scoped finding diff, rendered inline in
 * chat. Pure function of `FindingDiff` so one component serves all three sites:
 * a get_findings result, the apply confirmation card, and the apply success
 * card. Presentation reuses ValueDiff from the findings page; the only
 * chat-specific logic is deriving "before" from the projected rep entity.
 */
export function DiffCard({ diff }: DiffCardProps) {
  const variants = diff.current_value_variants;
  const isBulk = variants != null;
  // The badge names the row for a HUMAN, so it prefers the display label and
  // falls back to the identifier (SC-3032). Display only — `label` is not
  // unique, so anything that RESOLVES an entity still uses `identifier`.
  const repName = diff.rep_entity.label ?? diff.rep_entity.identifier;
  const current = isBulk ? variants.join(' / ') : stringifyCell(diff.rep_entity.data[diff.field]);
  return (
    <div style={cardStyle} data-testid="diff-card">
      <ScopeRow>
        {isBulk ? (
          <Badge tone="info">
            {variants.length} value{variants.length === 1 ? '' : 's'}
          </Badge>
        ) : (
          repName != null && <Badge tone="info">{repName}</Badge>
        )}
        <FieldLabel>{diff.field}</FieldLabel>
      </ScopeRow>
      {isValueVerb(diff.action_verb) ? (
        <ValueDiff verb={diff.action_verb} current={current} proposed={diff.proposed_value} />
      ) : (
        // An op with no single proposed value (a find-and-replace, a rename, a
        // look-up): the current value and what it becomes, in words. Running it
        // through ValueDiff rendered the missing value as "<empty>", which reads
        // as "this will wipe the field" (SC-3444).
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <ValueChip text={current} wrap />
          <div style={{ fontSize: 13, color: t.text2, lineHeight: 1.5 }}>
            <span style={{ color: t.text3 }}>Becomes: </span>
            {/* `??` past the map too: the verb arrives off the wire, and a new BE
                verb must not leave "Becomes:" blank. */}
            {diff.descriptor || (OP_FALLBACK[diff.action_verb] ?? 'a change EKOM makes when you apply')}
          </div>
        </div>
      )}
    </div>
  );
}

type ValueVerb = 'replace' | 'fill' | 'remove' | 'flag_for_review';

function isValueVerb(verb: DiffActionVerb): verb is ValueVerb {
  return verb === 'replace' || verb === 'fill' || verb === 'remove' || verb === 'flag_for_review';
}

// Said when the backend sends no descriptor — what kind of change, never a value.
const OP_FALLBACK: Record<Exclude<DiffActionVerb, ValueVerb>, string> = {
  transform: 'a rule-based change to this field',
  schema: 'a change to the field itself',
  enrich: 'a value EKOM looks up when you apply',
};

const cardStyle: CSSProperties = {
  background: t.surface,
  border: `1px solid ${t.border}`,
  borderRadius: 8,
  padding: '12px 14px',
  marginTop: 6,
};
